import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import InputText from '@/components/UI/InputText/InputText';
import InputUser from '@/components/UI/InputUser/InputUser';
import Spinner from '@/components/UI/Spinner/Spinner';
import HeaderWithTooltip from '@/components/UI/TableListEditable/HeaderWithTooltip/HeaderWithTooltip';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import { DAY_DATE_FORMAT, HELP_AREA, VEHICLE_STATUS_COMMON_COUNT_COLUMNS, VEHICLE_STATUS_OVERVIEW_COLUMNS } from '@/helpers/constants';
import { getCRidBySubCRid, getFullFormattedDate, getPlantIdBySubCRId, parseObjectNegativeValuesToBlank } from '@/helpers/utils';
import useAppLanguage from '@/hooks/useAppLanguage';
import useError from '@/hooks/useError';
import { getCampaignInfo, getIsVANCR } from '@/redux/Campaign/campaign.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { setAreaHelp } from '@/redux/Help/help.action';
import { setSubCampaignPlantFeedback } from '@/redux/SubCampaign/subCampaign.action';
import { getSubCampaignInfo, getSubCampaignPlantFeedback } from '@/redux/SubCampaign/subCampaign.reducer';
import { getSettingsInfo } from '@/redux/User/user.reducer';
import campaignServices from '@/services/campaignServices';
import { Language, UserSettings, PlantFeedbackResponse, PlantFeedbackCounts, SubCampaignInfo } from '@/types';
import styles from './PlantFeedback.module.scss';

interface PlantFeedbackProps {
  headerLanguage: Language;
  settingsInfo: UserSettings;
  subCRPlantFeedbackRedux: PlantFeedbackResponse;
  subCampaignInfo: SubCampaignInfo;
  setSubCRPlantFeedbackRedux: (plantFeedback: PlantFeedbackResponse) => void;
  setHelpArea: (area: string) => void;
  isVANCR: boolean;
}

type PlantFeedbackRow = {
  key: string;
  subCr?: string;
  campaign?: string;
  dynamic?: string;
  blockingLocation?: string;
  thirdLevelRow?: boolean;
  fourthLevelRow?: boolean;
} & Partial<PlantFeedbackCounts>;

const PlantFeedback: React.FC<PlantFeedbackProps> = (props) => {
  const { headerLanguage, settingsInfo, setSubCRPlantFeedbackRedux, subCRPlantFeedbackRedux,
    setHelpArea, isVANCR, subCampaignInfo } = props;

  const { t } = useTranslation();
  const lang = useAppLanguage();
  const { handleCriticalError } = useError();
  const { id, version } = useParams<{ id?: string; version?: string }>();
  const isLoading = !subCRPlantFeedbackRedux?.subCr;

  const plantFeedbackColumns = useMemo(() => [
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.SUB_CR, title: t('plantFeedback.subCr') },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.CAMPAIGN, title: t('feedback.campaignNumber') },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.DYNAMIC, title: t('plantFeedback.dynamic') },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.BLOCKING_LOCATION, title: t('plantFeedback.blockingLocation') },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.VALIDATED_CARS, title: t('plantFeedback.validatedCars') },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.NO_REWORK, title: t('plantFeedback.noRework') },
    {
      name: VEHICLE_STATUS_OVERVIEW_COLUMNS.REWORK_NOT_STARTED,
      title: t('plantFeedback.reworkNotStarted'),
      headerComponent: HeaderWithTooltip,
      headerComponentParams: { tooltipText: t('plantFeedback.reworkNotStartedTooltip'), isWarningTooltip: false },
    },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.REWORK_OPEN, title: t('plantFeedback.reworkOpen') },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.REWORK_FINISHED, title: t('plantFeedback.reworkFinished') },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.OK_WITHOUT_REWORK, title: t('feedback.withoutRework') },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.REWORK_IN_FIELD, title: t('plantFeedback.reworkInField') },
  ], [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    campaignServices.getSubCampaignPlantFeedback(getCRidBySubCRid(id), getPlantIdBySubCRId(id), version)
      .then(setSubCRPlantFeedbackRedux)
      .catch(handleCriticalError);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tableData = useMemo<PlantFeedbackRow[]>(() => {
    const subCr = subCRPlantFeedbackRedux?.subCr;
    if (!subCr) return [];

    const counts = (source?: Partial<PlantFeedbackCounts>): Partial<PlantFeedbackCounts> => (
      parseObjectNegativeValuesToBlank(VEHICLE_STATUS_COMMON_COUNT_COLUMNS, source)
    );

    const rows: PlantFeedbackRow[] = [{
      key: subCr.subCrId,
      [VEHICLE_STATUS_OVERVIEW_COLUMNS.SUB_CR]: subCr.subCrId,
      ...counts(subCr),
    }];

    subCr.campaigns?.filter((campaign) => campaign.campaign).forEach((campaign) => {
      rows.push({
        key: `${subCr.subCrId}_${campaign.campaign}`,
        [VEHICLE_STATUS_OVERVIEW_COLUMNS.CAMPAIGN]: campaign.campaign,
        [VEHICLE_STATUS_OVERVIEW_COLUMNS.DYNAMIC]: campaign.dynamic
          ? t('plantFeedback.dynamicYes', { lng: headerLanguage?.toLowerCase() })
          : t('plantFeedback.dynamicNo', { lng: headerLanguage?.toLowerCase() }),
        thirdLevelRow: true,
        ...counts(campaign),
      });

      campaign.blockingLocations?.forEach((location) => {
        rows.push({
          key: `${subCr.subCrId}_${campaign.campaign}_${location.location}`,
          [VEHICLE_STATUS_OVERVIEW_COLUMNS.BLOCKING_LOCATION]: location.location,
          fourthLevelRow: true,
          ...counts(location),
        });
      });
    });

    return rows;
  }, [subCRPlantFeedbackRedux, headerLanguage, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="w-100">
      {isLoading && <Spinner isVisible isMask />}
      {!isLoading && (
        <AccordionPanel
          title={t('plantFeedback.titlePlant', { plant: subCampaignInfo?.plantName })}
          handleClick={() => setHelpArea(HELP_AREA.PLANT_FEEDBACK)}
        >
          <div className={styles.SectionRow}>
            <div className={styles.TwoColumns}>
              <InputText
                id="entry-created-on-subcr-plant-feedback"
                label={t('plantFeedback.entryCreatedOn')}
                value={getFullFormattedDate(subCRPlantFeedbackRedux?.creationDate, settingsInfo.timezone, DAY_DATE_FORMAT)}
                disabled
              />
            </div>
            <div className={styles.TwoColumns}>
              <InputUser
                id="campaign-manager-subcr-plant-feedback"
                label={t('campaignManager')}
                value={subCRPlantFeedbackRedux?.assignedUser?.summary}
                code={subCRPlantFeedbackRedux?.assignedUser?.code}
                title={t('campaignManager')}
                disabled
              />
            </div>
          </div>
          {tableData.length > 0 && (
            <div className={`w-100 ${styles.PlantFeedbackTable}`}>
              <TableListEditableNew
                list={tableData}
                columns={plantFeedbackColumns}
                isPaginated={false}
              />
            </div>
          )}
          {!isVANCR && (
            <>
              <h3>{t('feedback.approverTitle')}</h3>
              <div className={styles.SectionRow}>
                <div className={styles.FourColumns}>
                  <InputUser
                    id="e4-approver-subcr-plant-feedback"
                    label={t('recipients.supervisor')}
                    value={subCRPlantFeedbackRedux?.approversInformation?.supervisor?.summary}
                    code={subCRPlantFeedbackRedux?.approversInformation?.supervisor?.code}
                    title={t('recipients.causerCheckApprover')}
                    level={undefined}
                    disabled
                  />
                </div>
                <div className={styles.FourColumns}>
                  <InputUser
                    id="e3-approver-subcr-plant-feedback"
                    label={t('recipients.qmbApprover')}
                    value={subCRPlantFeedbackRedux?.approversInformation?.qmbApprover?.summary}
                    code={subCRPlantFeedbackRedux?.approversInformation?.qmbApprover?.code}
                    title={t('recipients.causerPreApprover')}
                    level={undefined}
                    disabled
                  />
                </div>
                <div className={styles.TwoColumns}>
                  <InputText
                    id="e3-comment-subcr-plant-feedback"
                    multiline
                    rows={4}
                    maxRows={5}
                    maxLength={512}
                    label={t('feedback.commentForApprovers')}
                    value={subCRPlantFeedbackRedux?.approversInformation?.commentForQmbApprover}
                    disabled
                  />
                </div>
              </div>
            </>
          )}
        </AccordionPanel>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  settingsInfo: getSettingsInfo(state),
  subCRPlantFeedbackRedux: getSubCampaignPlantFeedback(state),
  subCampaignInfo: getSubCampaignInfo(state),
  isVANCR: getIsVANCR(state),
});

const actionCreators = {
  setSubCRPlantFeedbackRedux: setSubCampaignPlantFeedback,
  setHelpArea: setAreaHelp,
};

export default connect(mapStateToProps, actionCreators)(PlantFeedback);
